import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Container,
  Grid,
  LinearProgress,
  Typography
} from "@mui/material";

import EventCard from "../layout/EventCard";
import { getData } from "./UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { AuthContext } from "../user/BCPAuthProvider";
import ListMap from "../other/ListMap";
import { ActiveEventsModal } from "../../pages/organize/EventsOrganize/EventsOrganizeDefault";

export default function HighlightedEvents() {
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.home"
  });

  const [events, setEvents] = useState(null);
  const [activeEvents, setActiveEvents] = useState(null);

  const authContext = useContext(AuthContext);

  const getEvents = async () => {
    const queryStringParameters = {};

    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryStringParameters.gameSystemId =
        getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
      queryStringParameters.gameType =
        getEnvironmentVariable("SINGLE_GAME_TYPE");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    if (navigator && navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 3000 // 3 second timeout
          });
        });

        queryStringParameters.lat = position.coords.latitude;
        queryStringParameters.long = position.coords.longitude;
      } catch (error) {
        // Continue without location data if geolocation fails
      }
    }

    const apiData = await getData(
      "/events/highlighted",
      { ...queryStringParameters },
      "v2"
    );

    return apiData || [];
  };

  useEffect(() => {
    const check = async () => {
      try {
        const apiEvents = await getEvents();
        if (apiEvents) {
          setEvents(apiEvents);
        } else {
          setEvents([]);
        }
      } catch (err) {
        setEvents([]);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container spacing={1} sx={{ p: 1, mb: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h3" sx={{ fontWeight: 700 }} gutterBottom>
            {t("highlightedEvents")}
          </Typography>
        </Grid>

        {!events ? (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        ) : (
          <>
            {events && events.length > 0 && (
              <Grid item xs={12}>
                <ListMap
                  name="events-map"
                  originalList={
                    events
                      ? events?.filter((event) => !event?.isOnlineEvent)
                      : []
                  }
                  sx={{ mb: 1 }}
                  mapSx={{ height: "30vh" }}
                  onClick={(lat, lng) => {
                    const eventArr = [];
                    events.forEach((event) => {
                      if (
                        event?.location?.point?.longitude === lng &&
                        event?.location?.point?.latitude === lat
                      ) {
                        eventArr.push(event);
                      }
                    });

                    setActiveEvents(eventArr);
                  }}
                  filterFunction={(event) =>
                    !event?.isOnlineEvent &&
                    (event?.location?.point?.latitude ||
                      event?.location?.point?.longitude)
                  }
                />
              </Grid>
            )}

            {events &&
              events.length > 0 &&
              events.map((event, index) => {
                return (
                  <Grid key={index} item xs={12} sm={6} md={4}>
                    <EventCard
                      event={event}
                      key={index}
                      authContext={authContext}
                    />
                  </Grid>
                );
              })}
          </>
        )}
        {events && (
          <Grid item xs={12} textAlign="center">
            <Button
              href="/events"
              variant={
                getEnvironmentVariable("COMPANY_FLAG") === "catan"
                  ? "contained"
                  : "outlined"
              }
              color="primary"
            >
              {events?.length > 0 ? t("seeMore") : t("findAnEvent")}
            </Button>
          </Grid>
        )}

        {activeEvents?.length && (
          <ActiveEventsModal
            activeEvents={activeEvents}
            setActiveEvents={setActiveEvents}
          />
        )}
      </Grid>
    </Container>
  );
}
